import React, { useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { SafetyCaseOwnerAssignDialog } from './SafetyCaseOwnerAssignDialog';
import { buyerOwnerDisplayName } from '../../domain/buyerFacing/conditionDisplayNames';

function isAssignedOwner(owner?: string | null, hasFollowupOwner?: boolean | null): boolean {
  if (hasFollowupOwner === false) return false;
  const text = String(owner || '').trim();
  if (!text) return false;
  return !['unassigned', 'not assigned', 'none', 'null', 'n/a'].includes(text.toLowerCase());
}

export function SafetyCaseOwnershipSection({
  safetyCaseId,
  owner,
  hasFollowupOwner,
  assignedAt,
  assignedBy,
  canAssign,
  onAssigned,
}: {
  safetyCaseId: string;
  owner?: string | null;
  hasFollowupOwner?: boolean | null;
  assignedAt?: string | null;
  assignedBy?: string | null;
  canAssign: boolean;
  onAssigned: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const assigned = useMemo(
    () => isAssignedOwner(owner, hasFollowupOwner),
    [hasFollowupOwner, owner],
  );
  const ownerLabel = assigned ? buyerOwnerDisplayName(owner) : 'Unassigned';

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }} data-testid="safety-case-ownership">
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.06 }}>
              Case owner
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700 }}
              color={assigned ? 'text.primary' : 'text.secondary'}
              data-testid="safety-case-ownership-value"
            >
              {ownerLabel}
            </Typography>
            {assigned && (assignedBy || assignedAt) ? (
              <Typography variant="caption" color="text.secondary" data-testid="safety-case-ownership-meta">
                {assignedBy ? `Assigned by ${buyerOwnerDisplayName(assignedBy)}` : 'Assigned'}
                {assignedAt ? ` · ${assignedAt}` : ''}
              </Typography>
            ) : null}
          </Box>
          {canAssign ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              data-testid="safety-case-ownership-assign"
            >
              {assigned ? 'Change owner' : 'Assign owner'}
            </Button>
          ) : null}
        </Stack>
      </CardContent>
      <SafetyCaseOwnerAssignDialog
        open={dialogOpen}
        safetyCaseId={safetyCaseId}
        currentOwner={assigned ? owner : ''}
        onClose={() => setDialogOpen(false)}
        onSaved={() => onAssigned()}
      />
    </Card>
  );
}
